import { createRootRoute, createRoute, useParams } from '@tanstack/react-router'
import type { ReactElement } from 'react'
import { App } from '@/app/App'
import { DateifyRouteSeo } from '@/app/RouteSeo'
import { AddSubtractDatePage } from '@/pages/add-subtract-date/AddSubtractDatePage'
import { AgeCalculatorPage } from '@/pages/age-calculator/AgeCalculatorPage'
import { BlogIndexPage } from '@/pages/blog/BlogIndexPage'
import { BlogPostPage } from '@/pages/blog/BlogPostPage'
import { BusinessDaysPage } from '@/pages/business-days/BusinessDaysPage'
import { CalendarPage } from '@/pages/calendar/CalendarPage'
import { DateDifferencePage } from '@/pages/date-difference/DateDifferencePage'
import { DaysUntilDatePage } from '@/pages/days-until-date/DaysUntilDatePage'
import { IndexPage } from '@/pages/index/IndexPage'
import { PomodoroPage } from '@/pages/pomodoro/PomodoroPage'
import { ProductionCalendarHubPage, ProductionCalendarYearPage } from '@/pages/production-calendar'
import { ShiftCalendarPage } from '@/pages/shift-calendar/ShiftCalendarPage'
import { StopwatchPage } from '@/pages/stopwatch/StopwatchPage'
import { TimerPage } from '@/pages/timer/TimerPage'
import { TimestampPage } from '@/pages/timestamp/TimestampPage'
import { TimeZonesPage } from '@/pages/time-zones/TimeZonesPage'
import { WeekNumberPage } from '@/pages/week-number/WeekNumberPage'
import { routePath, type RouteKey } from '@/seo/routes'
import { approvedProductionCalendars } from '@/shared/production-calendar'

const rootRoute = createRootRoute({
  component: App,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath.index,
  component: withRouteSeo('index', IndexPage),
})

const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath.calendar,
  component: withRouteSeo('calendar', CalendarHubPage),
})

const calendar2026Route = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath['calendar-2026'],
  component: withRouteSeo('calendar-2026', Calendar2026Page),
})

const calendar2027Route = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath['calendar-2027'],
  component: withRouteSeo('calendar-2027', Calendar2027Page),
})

const productionCalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath['production-calendar'],
  component: withRouteSeo('production-calendar', () => (
    <ProductionCalendarHubPage calendars={approvedProductionCalendars} />
  )),
})

const shiftCalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath['shift-calendar'],
  component: withRouteSeo('shift-calendar', ShiftCalendarPage),
})

const productionCalendarYearRoutes = approvedProductionCalendars.map((calendar) => {
  const routeKey = `production-calendar-${calendar.year}` as const

  return createRoute({
    getParentRoute: () => rootRoute,
    path: routePath[routeKey],
    component: withRouteSeo(routeKey, () => <ProductionCalendarYearPage calendar={calendar} />),
  })
})

const timestampRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath.timestamp,
  component: withRouteSeo('timestamp', TimestampPage),
})

const addSubtractDateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath['add-subtract-date'],
  component: withRouteSeo('add-subtract-date', AddSubtractDatePage),
})

const dateDifferenceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath['date-difference'],
  component: withRouteSeo('date-difference', DateDifferencePage),
})

const businessDaysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath['business-days'],
  component: withRouteSeo('business-days', BusinessDaysPage),
})

const ageCalculatorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath['age-calculator'],
  component: withRouteSeo('age-calculator', AgeCalculatorPage),
})

const daysUntilDateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath['days-until-date'],
  component: withRouteSeo('days-until-date', DaysUntilDatePage),
})

const weekNumberRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath['week-number'],
  component: withRouteSeo('week-number', WeekNumberPage),
})

const timeZonesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath['time-zones'],
  component: withRouteSeo('time-zones', TimeZonesPage),
})

const timerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath.timer,
  component: withRouteSeo('timer', TimerPage),
})

const pomodoroRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath.pomodoro,
  component: withRouteSeo('pomodoro', PomodoroPage),
})

const stopwatchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath.stopwatch,
  component: withRouteSeo('stopwatch', StopwatchPage),
})

const blogIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routePath.blog,
  component: BlogIndexPage,
})

const blogPostRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/blog/$slug',
  component: BlogPostRoute,
})

export const routeTree = rootRoute.addChildren([
  indexRoute,
  calendarRoute,
  calendar2026Route,
  calendar2027Route,
  shiftCalendarRoute,
  productionCalendarRoute,
  ...productionCalendarYearRoutes,
  timestampRoute,
  addSubtractDateRoute,
  dateDifferenceRoute,
  businessDaysRoute,
  ageCalculatorRoute,
  daysUntilDateRoute,
  weekNumberRoute,
  timeZonesRoute,
  timerRoute,
  pomodoroRoute,
  stopwatchRoute,
  blogIndexRoute,
  blogPostRoute,
])

function withRouteSeo(routeKey: RouteKey, Page: () => ReactElement) {
  return function DateifyRoute() {
    return (
      <DateifyRouteSeo routeKey={routeKey}>
        <Page />
      </DateifyRouteSeo>
    )
  }
}

function BlogPostRoute() {
  const { slug } = useParams({ from: '/blog/$slug' })
  return <BlogPostPage slug={slug} />
}

function Calendar2026Page() {
  return <CalendarPage initialYear={2026} />
}

function CalendarHubPage() {
  return <CalendarPage />
}

function Calendar2027Page() {
  return <CalendarPage initialYear={2027} />
}
